/**
 * Окно карточки Ozon на каркасе сущности.
 *
 * Деталь сводится к общей модели окна (`McardModel`); каркас, проверки, единые
 * вкладки, режим чтения и правка через журнал правок живут в `McardWindow`.
 * Здесь только своё у Ozon: разбор замечаний площадки, атрибуты со справочником
 * категории, комиссии по схемам, остатки по складам.
 *
 * Фото правятся общим редактором `CardMediaEditor` (перетаскивание, загрузка
 * файла, удаление, лимит), как у ВБ. Цена только для просмотра. Окно целиком
 * стоит на общем модуле `mp-card`; правка текста, фото и бренда идёт через
 * журнал правок (`windowEditApi`: ПИМ → журнал → площадка), а не прямыми
 * вызовами card-editor — тех методов в клиенте больше нет. Штрихкод и габариты
 * у ОЗОН только чтение.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useToastStore } from '../../../stores/toastStore'
import { contentStatusLabels } from '../../seo/workbench'
import { SYNC_STATE_LABELS } from '../../seo-v2/lib'
import { moderationLabels } from '../../seo-v2/publish'
import { EntityRelationsPanel } from '../../entity-relations'
// Словари состояний — те же, что в разделах, куда ведут переходы из карточки.
import { STATUS_INFO, publicationModeLabel } from '../../marketplace-catalog/draftColumns'
import { McardWindow } from '../../marketplace-catalog/components/mp-card/McardWindow'
import { Notice, Panel, ReadonlyValue, formatDateTime } from '../../marketplace-catalog/components/mp-card/McardParts'
import {
  formatRub,
  type McardCharacteristic,
  type McardIssue,
  type McardModel,
} from '../../marketplace-catalog/components/mp-card/model'
import { ozonAPI } from '../api'
import { categoryOf, ozStatusLabel, ozStatusVariant, rowKeyOf } from './columns'
import type { OzCatalogProduct } from './types'

interface OzProductModalProps {
  sellerId: string | null
  sellerName?: string | null
  offerId: string | null
  keys: string[]
  rows: OzCatalogProduct[]
  onNavigate: (key: string) => void
  onClose: () => void
  onSync: () => void
  /** Окно открыто навигатором по цепочке: есть куда вернуться (кнопка и Alt+←). */
  canGoBack?: boolean
  onBack?: () => void
}

/**
 * Замечание Ozon в пригодном для чтения виде.
 *
 * ГДЕ ТЕКСТ. Площадка кладёт объяснение внутрь `texts`, а на верхнем уровне
 * оставляет только код. Сверка с боевой базой 20.08.2026: поля `description`
 * верхнего уровня нет ни у одной из 22 176 карточек с замечаниями, тогда как
 * `texts.description` заполнен у 22 175. Пока вложенность не разбиралась,
 * оператор видел «double_without_merger_offer» вместо готового русского
 * объяснения, которое площадка прислала рядом.
 *
 * ПОЧЕМУ ВАЖЕН УРОВЕНЬ. Из тех же карточек 15 560 несут только
 * `ERROR_LEVEL_WARNING`. Называть их ошибками и красить красным — обещать
 * поломку там, где площадка всего лишь предупреждает.
 */
export interface OzIssue {
  level: 'error' | 'warning'
  title: string
  text: string
}

export const normalizeOzIssue = (raw: any): OzIssue => {
  const texts = raw?.texts || {}
  const text = String(
    texts.description || texts.short_description || texts.message
      || raw?.description || raw?.message || '',
  ).trim()
  const title = String(
    texts.attribute_name || raw?.attribute_name || raw?.field || raw?.code || 'Замечание',
  ).trim()
  return {
    level: String(raw?.level || '').toUpperCase().includes('WARNING') ? 'warning' : 'error',
    title: title || 'Замечание',
    // Код — последнее прибежище: он хотя бы даёт что искать в поддержке.
    text: text || String(raw?.code || 'Площадка не пояснила замечание'),
  }
}

/** Текст имеет приоритет, 0 и false — значения; ID словаря годится только положительный. */
const ozValueText = (value: any): string => {
  const text = value?.value
  if (text !== null && text !== undefined && String(text).trim() !== '') return String(text)
  const dictionaryId = Number(value?.dictionary_value_id)
  return Number.isFinite(dictionaryId) && dictionaryId > 0 ? String(dictionaryId) : ''
}

const attributeValue = (attribute: any): string => {
  const values = Array.isArray(attribute?.values) ? attribute.values : []
  if (values.length) return values.map(ozValueText).filter((value: string) => value !== '').join(', ')
  return ozValueText(attribute)
}

const OZ_CATEGORY_TYPE = 8229
const OZ_CATEGORY_TYPE_REASON = 'Тип задаётся категорией Ozon; из окна не редактируется и не отправляется'

/** Идентификаторы атрибутов, которые правятся своими полями (бренд, название, описание). */
const DEDICATED_ATTRIBUTES = [85, 4180, 4191]

export const ozCharacteristics = (product: OzCatalogProduct | null): McardCharacteristic[] => {
  if (!product) return []
  const labels = Array.isArray(product.attribute_labels) ? product.attribute_labels : []
  const current: any[] = Array.isArray(product.attributes) ? product.attributes : []
  const valueOf = (id: number): string => {
    const found = current.find((item) => Number(item.id ?? item.attribute_id) === id)
    return found ? attributeValue(found) : ''
  }
  const rows: McardCharacteristic[] = labels
    .filter((label) => !DEDICATED_ATTRIBUTES.includes(Number(label.key)))
    .map((label) => ({
      key: String(label.key),
      targetId: Number(label.key),
      name: String(label.name),
      required: Boolean(label.required),
      value: valueOf(Number(label.key)),
      hint: `Ozon ID ${label.key}${label.type ? ` · ${label.type}` : ''}`,
    }))
  const known = new Set(rows.map((row) => row.key))
  for (const attribute of current) {
    const id = Number(attribute.id ?? attribute.attribute_id)
    if (DEDICATED_ATTRIBUTES.includes(id) || known.has(String(id))) continue
    const label = labels.find((item) => Number(item.key) === id)
    rows.push({
      key: String(id),
      targetId: Number.isFinite(id) ? id : null,
      name: label?.name || `ID ${id}`,
      required: Boolean(label?.required),
      value: attributeValue(attribute),
      hint: `Ozon ID ${id}${label ? '' : ' · имя не загружено'}`,
    })
  }
  // Ozon хранит тип категории отдельно от attributes[]. Не создаём ложную пустую строку.
  const typeId = Number(product.type_id)
  const categoryType = Number.isFinite(typeId) && typeId > 0
    ? String(product.type_name ?? '').trim() || String(typeId)
    : ''
  let typeRow = rows.find((row) => row.targetId === OZ_CATEGORY_TYPE)
  if (!typeRow && categoryType) {
    typeRow = { key: String(OZ_CATEGORY_TYPE), targetId: OZ_CATEGORY_TYPE, name: 'Тип', required: false, value: categoryType }
    rows.push(typeRow)
  }
  if (typeRow) {
    if (!typeRow.value.trim()) typeRow.value = categoryType
    typeRow.system = { source: 'ozon_category_type', lockedReason: OZ_CATEGORY_TYPE_REASON }
    typeRow.hint = 'Ozon ID 8229 · Тип категории Ozon · только чтение'
  }
  return rows
}

export const buildOzModel = (
  product: OzCatalogProduct,
  offerId: string,
  sellerName?: string | null,
  sellerId?: string | null,
): McardModel => {
  const images = (Array.isArray(product.images) ? product.images : []).filter(Boolean)
  const photos =
    product.primary_image && !images.includes(product.primary_image) ? [product.primary_image, ...images] : images
  const issues: McardIssue[] = (Array.isArray(product.errors) ? product.errors : []).map(normalizeOzIssue)
  const seo = product.seo_channel || null
  const publication = product.publication || null
  if (seo?.moderation_message) issues.push({ level: 'warning', title: 'Модерация', text: String(seo.moderation_message) })
  if (publication?.error) issues.push({ level: 'warning', title: 'Публикация', text: String(publication.error) })
  const dims = [product.depth, product.width, product.height]
  const dimensionsText = dims.some((value) => value != null)
    ? `${dims.map((value) => value ?? '—').join(' × ')} мм · ${product.weight ?? '—'} г`
    : null
  const stock =
    product.total_stock === null || product.total_stock === undefined ? null : Number(product.total_stock)
  // Каталог отдаёт строку: сырой объект visibility из Ozon нормализует сервис синхронизации.
  const visibility = typeof product.visibility === 'string' ? product.visibility.trim().toUpperCase() : ''
  const archived = Boolean(product.is_archived) || visibility === 'ARCHIVED'
  const shown = product.final_price ?? product.marketing_price ?? product.price
  return {
    platform: 'ozon',
    article: product.offer_id || offerId,
    title: String(product.name || ''),
    description: String(product.description || ''),
    brand: product.brand ? String(product.brand) : null,
    statusLabel: ozStatusLabel(product),
    statusVariant: ozStatusVariant(product.status, product.is_archived),
    showcase: archived
      ? {
          tone: 'bad',
          value: 'в архиве',
          onShowcase: false,
          problem: {
            title: 'Карточка в архиве площадки — на витрине её нет',
            hint: 'Покупатель её не видит. Вернуть из архива можно в кабинете Ozon.',
          },
        }
      : visibility === 'VISIBLE'
        ? { tone: 'ok', value: 'на витрине', onShowcase: true }
        : visibility === 'INVISIBLE'
          ? {
              tone: 'warn', value: 'не на витрине', onShowcase: false,
              problem: {
                title: 'Карточка скрыта на витрине Ozon',
                hint: 'Ozon сообщил, что покупатель карточку не видит. Проверьте статус и замечания в кабинете Ozon.',
              },
            }
          : {
              tone: 'warn', value: 'видимость неизвестна', onShowcase: false,
              problem: {
                title: 'Ozon не подтвердил видимость карточки',
                hint: 'В зеркале ПИМ нет известного статуса видимости. Проверьте карточку в кабинете Ozon или обновите данные.',
              },
            },
    photos,
    price: {
      shown: shown != null && Number.isFinite(Number(shown)) ? Number(shown) : null,
      old: Number(product.old_price) > 0 ? Number(product.old_price) : null,
      discountPercent: Number(product.discount_percent) > 0 ? Number(product.discount_percent) : null,
      currency: product.currency_code || 'RUB',
    },
    stock: stock !== null && Number.isFinite(stock) ? stock : null,
    stockWhy: 'остатки по складам не пришли',
    issues,
    characteristics: ozCharacteristics(product),
    specsKnown: Array.isArray(product.attribute_labels) && product.attribute_labels.length > 0,
    lastSyncAt: product.last_sync_at ?? null,
    platformUrl: product.sku ? `https://www.ozon.ru/product/${product.sku}` : null,
    idMeta: [
      { label: 'Ozon ID', value: product.product_id ? String(product.product_id) : null, mono: true, copy: true },
      { label: 'SKU', value: product.sku ? String(product.sku) : null, mono: true, copy: true },
    ],
    barcode: product.barcode || product.barcodes?.[0] || null,
    cabinetLabel: sellerName || (sellerId ? `#${sellerId}` : ''),
    designCode: product.design_code ? String(product.design_code) : null,
    pimArticle: product.pim_article ?? null,
    themeNumber: product.theme_number ?? null,
    dimensionsText,
    categoryName: categoryOf(product) || null,
    seoSyncState: seo?.sync_state ?? null,
    seoLastPublishedAt: seo?.last_published_at ?? null,
    bundleExternalId: product.merge_label ?? null,
  }
}

// ── Комиссии таблицей ───────────────────────────────────────────────────────

const COMMISSION_LABELS: Record<string, string> = {
  sale_schema: 'Схема',
  percent: 'Комиссия, %',
  delivery_amount: 'Доставка, ₽',
  return_amount: 'Возврат, ₽',
  value: 'Значение',
}

const commissionCell = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'нет данных'
  if (typeof value === 'number') {
    const text = value.toLocaleString('ru-RU')
    if (/percent/i.test(key)) return `${text} %`
    if (/amount|price|cost|fee/i.test(key)) return `${text} ₽`
    return text
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** Комиссии по схемам: массив записей → строки таблицы, одиночный объект → одна строка. */
export const commissionRows = (value: unknown): Array<Record<string, unknown>> => {
  if (!value) return []
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object') as Array<Record<string, unknown>>
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    const entries = Object.entries(record)
    // Словарь «схема → запись»: каждая схема своей строкой.
    if (entries.length > 0 && entries.every(([, item]) => item && typeof item === 'object' && !Array.isArray(item))) {
      return entries.map(([schema, item]) => ({ sale_schema: schema, ...(item as Record<string, unknown>) }))
    }
    return [record]
  }
  return []
}

export const OzProductModal: React.FC<OzProductModalProps> = ({
  sellerId,
  sellerName,
  offerId,
  keys,
  rows,
  onNavigate,
  onClose,
  onSync,
  canGoBack,
  onBack,
}) => {
  const { showToast } = useToastStore()
  const [detail, setDetail] = useState<OzCatalogProduct | null>(null)
  const [loading, setLoading] = useState(false)
  /** Первая загрузка полной карточки закончилась (успехом или ошибкой): до этого окно показывает скелетоны. */
  const [detailSettled, setDetailSettled] = useState(false)

  const listRow = useMemo(
    () => rows.find((row) => (row.offer_id || String(row.id)) === offerId) || null,
    [rows, offerId],
  )
  const product = useMemo<OzCatalogProduct | null>(
    () => (listRow || detail ? ({ ...(listRow || {}), ...(detail || {}) } as OzCatalogProduct) : null),
    [listRow, detail],
  )
  const linkIsRowId = Boolean(listRow) && !listRow?.offer_id

  const loadDetail = useCallback(
    async (refresh = false) => {
      if (!sellerId || !offerId || linkIsRowId) {
        setDetailSettled(true)
        return
      }
      setLoading(true)
      try {
        const response = refresh
          ? await ozonAPI.refreshProductCard(Number(sellerId), offerId)
          : await ozonAPI.getProductByOfferId(Number(sellerId), offerId)
        const card = response.data?.data ?? response.data
        if (card && !card.error) setDetail(card)
        if (refresh) {
          showToast({
            type: 'success',
            title: 'Карточка сверена с площадкой',
            message: 'Перечитана одна карточка Ozon',
            duration: 3500,
          })
        }
      } catch (error: any) {
        showToast({
          type: 'error',
          title: 'Не удалось загрузить карточку',
          message: error?.response?.data?.error?.message || error?.message,
          duration: 5000,
        })
      } finally {
        setLoading(false)
        setDetailSettled(true)
      }
    },
    [linkIsRowId, offerId, sellerId, showToast],
  )

  useEffect(() => {
    setDetail(null)
    setDetailSettled(false)
    void loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, offerId])

  // Соседние карточки подгружаются заранее: листание стрелками без ожидания.
  useEffect(() => {
    if (!sellerId || !offerId || !detail) return
    const activeRow = rows.find((row) => row.offer_id === offerId)
    const index = activeRow ? keys.indexOf(rowKeyOf(activeRow)) : -1
    for (const key of [keys[index - 1], keys[index + 1]].filter(Boolean)) {
      const neighbor = rows.find((row) => rowKeyOf(row) === key)
      if (neighbor?.offer_id) void ozonAPI.getProductByOfferId(Number(sellerId), neighbor.offer_id).catch(() => undefined)
    }
  }, [sellerId, offerId, detail, keys, rows])

  const model = useMemo(
    () => (product && offerId ? buildOzModel(product, offerId, sellerName, sellerId) : null),
    [product, offerId, sellerName, sellerId],
  )

  if (!offerId) return null

  const seo = product?.seo_channel || null
  const publication = product?.publication || null
  const commissions = commissionRows(product?.commissions)
  const commissionColumns = Array.from(new Set(commissions.flatMap((record) => Object.keys(record)))).sort(
    (a, b) => (a === 'sale_schema' ? -1 : b === 'sale_schema' ? 1 : 0),
  )
  const warehouseStocks = Object.entries((product?.warehouse_stocks as Record<string, number> | null) ?? {})

  const overviewExtra = (
    <>
      <Panel title="Состояние на Ozon">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <ReadonlyValue label="Номер карточки" value={product?.product_id || null} mono hint="Ozon ID — идентификатор площадки, не изменяется" />
          <ReadonlyValue label="SKU" value={product?.sku || null} mono hint="Товарный номер на витрине" />
          <ReadonlyValue
            label="Категория"
            value={product ? categoryOf(product) || null : null}
            hint="После создания карточки категорию сменить нельзя"
          />
          <ReadonlyValue label="Статус витрины" value={product ? ozStatusLabel(product) : null} />
        </div>
      </Panel>
      <Panel title="Публикация и SEO-канал">
        <div className="grid gap-4 sm:grid-cols-3">
          <ReadonlyValue
            label="Статус публикации"
            value={publication?.status ? STATUS_INFO[publication.status]?.label || publication.status : null}
            why="публикаций нет"
          />
          <ReadonlyValue
            label="Режим"
            value={publication ? publicationModeLabel(publication.publication_mode) : null}
            why="публикаций нет"
          />
          <ReadonlyValue
            label="Опубликовано"
            value={publication?.published_at ? formatDateTime(publication.published_at) : null}
            why="публикаций нет"
          />
          <ReadonlyValue
            label="Контент"
            value={seo?.status ? contentStatusLabels[seo.status] || seo.status : null}
            why="SEO-контента нет"
          />
          <ReadonlyValue
            label="Синхронизация"
            value={seo?.sync_state ? SYNC_STATE_LABELS[seo.sync_state] || seo.sync_state : null}
            why="SEO-контента нет"
          />
          <ReadonlyValue
            label="Модерация"
            value={seo?.moderation_status ? moderationLabels[seo.moderation_status]?.label || seo.moderation_status : null}
            why="на модерацию не отправлялось"
          />
        </div>
        {seo?.last_published_at && (
          <p className="mt-3 text-[11px] text-gray-500 dark:text-zinc-400">
            Последняя публикация SEO: {formatDateTime(seo.last_published_at)}.
          </p>
        )}
      </Panel>
    </>
  )

  const priceContent = (
    <div className="space-y-4">
      <Panel title="Цена Ozon">
        <div className="grid gap-4 sm:grid-cols-4">
          <ReadonlyValue label="Цена" value={product?.price != null ? formatRub(product.price) : null} why="цена не пришла" />
          <ReadonlyValue label="Старая цена" value={Number(product?.old_price) > 0 ? formatRub(product?.old_price) : null} why="старой цены нет" />
          <ReadonlyValue label="Минимальная" value={Number(product?.min_price) > 0 ? formatRub(product?.min_price) : null} why="минимальная цена не задана" />
          <ReadonlyValue label="Валюта" value={product?.currency_code || null} />
          <ReadonlyValue label="Маркетинговая" value={product?.marketing_price != null ? formatRub(product.marketing_price) : null} why="площадка не прислала" />
          <ReadonlyValue label="Итоговая" value={product?.final_price != null ? formatRub(product.final_price) : null} why="площадка не прислала" />
          <ReadonlyValue label="НДС" value={product?.vat != null ? `${product.vat} %` : null} why="НДС не пришёл" />
        </div>
      </Panel>
      <Panel title="Комиссии по схемам">
        {commissions.length === 0 ? (
          <Notice>Нет данных о комиссиях.</Notice>
        ) : (
          <div className="overflow-hidden rounded-md border border-gray-200 dark:border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 dark:bg-zinc-950 dark:text-zinc-400">
                <tr>
                  {commissionColumns.map((column) => (
                    <th key={column} className="px-3 py-2 font-medium">
                      {COMMISSION_LABELS[column] ?? column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                {commissions.map((record, index) => (
                  <tr key={index}>
                    {commissionColumns.map((column) => (
                      <td key={column} className="px-3 py-2 tabular-nums text-gray-800 dark:text-zinc-200">
                        {commissionCell(column, record[column])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <Panel title="Остатки по складам">
        {warehouseStocks.length === 0 ? (
          <Notice>Нет данных: площадка не прислала остатки по складам.</Notice>
        ) : (
          <div className="overflow-hidden rounded-md border border-gray-200 dark:border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 dark:bg-zinc-950 dark:text-zinc-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Склад</th>
                  <th className="px-3 py-2 text-right font-medium">Остаток, шт.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                {warehouseStocks.map(([warehouse, quantity]) => (
                  <tr key={warehouse}>
                    <td className="px-3 py-2 text-gray-800 dark:text-zinc-200">{warehouse}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-800 dark:text-zinc-200">
                      {Number(quantity).toLocaleString('ru-RU')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  )

  return (
    <McardWindow
      platform="ozon"
      sellerId={sellerId ? Number(sellerId) : null}
      sellerName={sellerName}
      windowKey={offerId}
      model={model}
      loading={!detail && !detailSettled}
      keys={keys}
      activeKey={product ? rowKeyOf(product) : null}
      onNavigate={onNavigate}
      onClose={onClose}
      canGoBack={canGoBack}
      onBack={onBack}
      onRefresh={() => loadDetail(true)}
      refreshing={loading}
      onSync={onSync}
      onReloadDetail={() => void loadDetail()}
      linkParam="offer"
      mediaEditable
      mediaHint={
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Набор уходит на Ozon целиком в том порядке, который задан здесь; первое фото — главное.
        </p>
      }
      mediaExtra={
        <Panel title="Дополнительные материалы">
          <div className="grid gap-4 sm:grid-cols-3">
            <ReadonlyValue label="360°" value={product?.images_360?.length ? `${product.images_360.length} шт.` : null} why="материалов нет" />
            <ReadonlyValue label="PDF" value={product?.pdf_list?.length ? `${product.pdf_list.length} шт.` : null} why="материалов нет" />
            <ReadonlyValue label="Изображение цвета" value={product?.color_image ? 'есть' : null} why="материала нет" />
          </div>
        </Panel>
      }
      priceContent={priceContent}
      overviewExtra={overviewExtra}
      relations={
        <EntityRelationsPanel
          article={product?.pim_article}
          designCode={product?.design_code}
          current={{ kind: 'ozon', id: String(product?.product_id || '') }}
        />
      }
      bundleExternal={{ id: model?.bundleExternalId, label: 'Метка склейки' }}
    />
  )
}

export default OzProductModal
